'use client'

import { useState, useCallback } from 'react'

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const LS_ENABLED = 'weekly-reflection-enabled'
const LS_DAY = 'weekly-reflection-day'

function readLS(key: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  try {
    return localStorage.getItem(key) ?? fallback
  } catch {
    return fallback
  }
}

function writeLS(key: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(key, value)
  } catch {
    // ignore quota errors
  }
}

// ---------------------------------------------------------------------------
// Day options
// ---------------------------------------------------------------------------

const DAY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ReflectionSettings() {
  const [enabled, setEnabled] = useState(
    () => readLS(LS_ENABLED, 'true') === 'true',
  )
  const [day, setDay] = useState(() => readLS(LS_DAY, '1'))

  const handleToggle = useCallback(() => {
    const next = !enabled
    setEnabled(next)
    writeLS(LS_ENABLED, String(next))
  }, [enabled])

  const handleDayChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value
      setDay(value)
      writeLS(LS_DAY, value)
    },
    [],
  )

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Weekly Reflections</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Auto-generated journal entries that summarize your week and prompt
          reflection
        </p>
      </div>
      <div className="space-y-4 rounded-lg border p-6">
        {/* Toggle */}
        <div className="flex items-center justify-between">
          <label
            htmlFor="weekly-reflection-toggle"
            className="cursor-pointer text-sm"
          >
            Generate weekly reflections
          </label>
          <button
            id="weekly-reflection-toggle"
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={handleToggle}
            className={`focus-visible:ring-ring relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:ring-2 focus-visible:outline-none ${
              enabled ? 'bg-accent' : 'bg-muted'
            }`}
            aria-label="Toggle weekly reflections"
          >
            <span
              className={`bg-foreground pointer-events-none inline-block h-5 w-5 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${
                enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Day selector */}
        {enabled && (
          <div className="flex items-center justify-between">
            <label
              htmlFor="weekly-reflection-day"
              className="text-muted-foreground text-sm"
            >
              Day of week
            </label>
            <select
              id="weekly-reflection-day"
              value={day}
              onChange={handleDayChange}
              className="bg-muted text-foreground focus:ring-ring rounded-md border-none px-3 py-1.5 text-sm focus:ring-2 focus:outline-none"
              aria-label="Weekly reflection day"
            >
              {DAY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  )
}
